import { useEffect, useState } from 'react';
import './DevelopmentJourney.css';

function formatDate(value, options = { year: 'numeric', month: 'short', day: 'numeric' }) {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : new Intl.DateTimeFormat('en', options).format(date);
}

function milestoneDetail(label) {
  if (label === 'Development started') return 'Earliest commit in the available repository history';
  if (label === 'Latest code activity') return 'Most recent recorded commit';
  if (label === 'Release history began') return 'First published release found in the repository';
  if (label === 'Latest release published') return 'Most recent published release';
  return 'Recorded in the repository history';
}

export default function DevelopmentJourney() {
  const [journey, setJourney] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState('Development history is temporarily unavailable.');

  useEffect(() => {
    const controller = new AbortController();

    fetch('/api/development-journey', { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((body) => {
            const messages = {
              github_unauthorized: 'GitHub rejected the configured token. Check that it is valid and has read access to the private repository.',
              github_forbidden_or_rate_limited: 'GitHub denied access or limited the request. Check the token permissions and GitHub rate limits.',
              github_repository_not_found_or_not_accessible: 'GitHub could not find the repository with these settings, or the token cannot access it. Check the owner and repository values.',
              github_repository_has_no_commits: 'The repository has no commits available yet.',
              github_no_usable_commit_dates: 'GitHub returned no usable commit dates. Check that the repository has commit history.',
              github_network_error: 'The server could not connect to GitHub. Please try again later.',
            };
            setErrorMessage(messages[body.code] || 'Development history is temporarily unavailable. Check the Vercel function logs.');
            throw new Error('Development history unavailable');
          });
        }
        return response.json();
      })
      .then((data) => setJourney(data))
      .catch((error) => {
        if (error.name !== 'AbortError') setJourney(null);
      })
      .finally(() => setLoaded(true));

    return () => controller.abort();
  }, []);

  const startDate = journey && formatDate(journey.since, { year: 'numeric', month: 'long' });
  const latestDate = journey && formatDate(journey.lastUpdated);
  const milestones = journey?.milestones || [];

  return (
    <section className="journey" id="journey">
      <div className="journey-inner">
        <div className="journey-heading reveal">
          <div className="journey-kicker">Product development journey</div>
          <h2>Development <em>Journey</em></h2>
          <p>
            {startDate
              ? `Built from an original idea. Evolving into a complete document workspace. Actively developed since ${startDate}.`
              : 'A clear record of how TAGpdf has been built and continuously improved.'}
          </p>
        </div>

        <div className="journey-stats" aria-live="polite">
          {journey && startDate && latestDate ? (
            <>
              <div className="journey-stat">
                <span className="journey-stat-value">{startDate}</span>
                <span className="journey-stat-label">Development started</span>
              </div>
              <div className="journey-stat">
                <span className="journey-stat-value">{journey.commits.toLocaleString()}</span>
                <span className="journey-stat-label">Commits</span>
              </div>
              {journey.contributors != null && (
                <div className="journey-stat">
                  <span className="journey-stat-value">{journey.contributors.toLocaleString()}</span>
                  <span className="journey-stat-label">Contributors</span>
                </div>
              )}
              <div className="journey-stat">
                <span className="journey-stat-value">{latestDate}</span>
                <span className="journey-stat-label">Last updated</span>
              </div>
            </>
          ) : (
            <div className="journey-status">
              {loaded
                ? errorMessage
                : 'Loading verified development history…'}
            </div>
          )}
        </div>

        {journey && milestones.length > 0 && (
          <div className="journey-timeline-wrap">
            <div className="journey-timeline-heading">
              <h3>Development milestones</h3>
              <div className="journey-sync" role="status">
                <span className="journey-sync-dot" aria-hidden="true" />
                <span>GitHub history loaded</span>
                <span className="journey-sync-divider" aria-hidden="true">/</span>
                <span>Latest activity {latestDate}</span>
              </div>
            </div>
            <div className="journey-timeline" aria-label="TAGpdf development milestones">
              {milestones.map((milestone, index) => (
                <article
                  className={`journey-milestone${milestone.label === 'Latest code activity' ? ' is-latest' : ''}`}
                  key={`${milestone.label}-${milestone.date}`}
                  style={{ '--milestone-index': index }}
                >
                  <div className="journey-marker" aria-hidden="true" />
                  <div className="journey-event">
                    <time className="journey-date" dateTime={milestone.date}>
                      {formatDate(milestone.date)}
                    </time>
                    <h4>{milestone.label}</h4>
                    <p>{milestoneDetail(milestone.label)}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}